"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChartControlsProps } from "../../lib/interface/ChartComponentProps.interface";
import { IndicatorSelector } from "../selectors/IndicatorSelector";
import { ClerkUserButton } from "../auth/ClerkUserButton";
import { ActionButton } from "../buttons/ActionButton";

export const ChartControls: React.FC<ChartControlsProps> = ({
  selectedPair,
  selectedTimeframe,
  selectedExchange,
  pairs = [], // Provide default empty array
  timeframes = [], // Provide default empty array
  exchanges = [], // Provide default empty array
  onPairChange,
  onTimeframeChange,
  onExchangeChange,
  onToggleIndicator,
  indicatorEnabledStates,
  allowedIndicatorIds,
  onDebugClick,
  onBacktestClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideExchange = exchanges.length === 1;

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isMenuOpen]);

  const pairSelect = (
    <select
      value={selectedPair}
      onChange={(e) => onPairChange(e.target.value)}
      className="block w-full md:w-40 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
      disabled={pairs.length === 0}
    >
      {pairs.length === 0 && <option value="">No pairs available</option>}
      {pairs.map((pair) => (
        <option key={pair} value={pair}>
          {pair}
        </option>
      ))}
    </select>
  );

  const timeframeSelect = (
    <select
      value={selectedTimeframe}
      onChange={(e) => onTimeframeChange(e.target.value)}
      className="block w-full md:w-24 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
      disabled={timeframes.length === 0}
    >
      {timeframes.length === 0 && <option value="">No timeframes</option>}
      {timeframes.map((timeframe) => (
        <option key={timeframe} value={timeframe}>
          {timeframe}
        </option>
      ))}
    </select>
  );

  const exchangeSelect = (
    <select
      value={selectedExchange}
      onChange={(e) => onExchangeChange(e.target.value)}
      className="block w-full md:w-40 h-10 bg-slate-800 text-white border border-gray-600 rounded-md py-2 px-3"
      disabled={exchanges.length === 0}
    >
      {exchanges.length === 0 && <option value="">No exchanges</option>}
      {exchanges.map((exchange) => (
        <option key={exchange} value={exchange}>
          {exchange}
        </option>
      ))}
    </select>
  );

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <ActionButton
        onClick={onBacktestClick}
        label="Backtest"
        variant="success"
        className="flex-1 min-w-[140px]"
      />
    </div>
  );

  return (
    <div ref={containerRef} className="absolute inset-x-0 top-0 z-[100]">
      <div className="relative bg-slate-900 border-b border-gray-800 shadow-lg min-h-[56px] md:min-h-[72px] flex flex-col justify-center rounded-t-lg">
        <div className="flex items-center justify-between px-3 py-2 md:hidden">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-200">
            <img src="/mirage.png" alt="Volumetric" className="h-7 w-auto" />
            <span>Mirage</span>
          </div>
          <button
            className="rounded bg-gray-700 px-3 py-1 text-sm text-white"
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle controls menu"
          >
            <span aria-hidden className="text-lg leading-none">
              ☰
            </span>
          </button>
        </div>

        <div className="hidden md:flex md:flex-row md:justify-between md:items-center md:px-4 md:py-3 md:gap-6">
          <div className="flex items-center gap-3">
            <img src="/mirage.png" alt="Volumetric" className="h-8 w-auto" />
            <span className="text-sm font-semibold text-neutral-200">
              Mirage
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ClerkUserButton onDebugClick={onDebugClick} />
          </div>
        </div>

        <div
          className={`md:hidden absolute left-0 right-0 top-full z-[110] px-3 pb-3 origin-top bg-slate-900/95 backdrop-blur-sm transition-all duration-300 ease-in-out shadow-lg ${
            isMenuOpen
              ? "opacity-100 translate-y-0 max-h-[480px] overflow-visible"
              : "opacity-0 -translate-y-2 max-h-0 overflow-hidden pointer-events-none"
          }`}
        >
          <div className="flex flex-col gap-4">
            <div
              className={
                hideExchange
                  ? "grid [grid-template-columns:2fr_1fr] gap-3"
                  : "grid [grid-template-columns:2fr_1fr_2fr] gap-3"
              }
            >
              {pairSelect}
              {timeframeSelect}
              {!hideExchange && exchangeSelect}
            </div>

            <IndicatorSelector
              onToggleIndicator={onToggleIndicator}
              enabledStates={indicatorEnabledStates}
              allowedIndicatorIds={allowedIndicatorIds}
              className="w-full"
            />

            <div className="rounded-lg border border-gray-800 bg-slate-900/70 p-3 shadow-inner">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Actions
              </div>
              {actions}
            </div>

            <div className="rounded-lg border border-gray-800 bg-slate-900/70 p-3 shadow-inner">
              <div className="flex justify-end items-center">
                <ClerkUserButton onDebugClick={onDebugClick} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
